<template>
  <div
    v-if="authStore.isAuthenticated"
    ref="rootRef"
    class="relative hidden md:block"
  >
    <button
      type="button"
      class="inline-flex text-gray-300 icon-btn hover:text-white items-center justify-center relative"
      :aria-label="$t('common.notifications')"
      @click="toggleOpen"
    >
      <i class="fas fa-bell" />
      <span
        v-if="unreadCountDisplay > 0"
        class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] leading-[18px] font-semibold text-center"
      >
        {{ unreadCountDisplay > 99 ? '99+' : unreadCountDisplay }}
      </span>
    </button>

    <div
      v-if="isOpen"
      class="absolute right-0 z-50 mt-2 w-96 max-w-[90vw] bg-[#09060f] border border-white/15 rounded-md shadow-lg shadow-black/70 overflow-hidden"
    >
      <div class="flex items-center justify-between px-4 py-3">
        <h3 class="text-sm font-semibold text-white">
          {{ $t('common.notifications') }}
        </h3>
        <button
          type="button"
          class="text-xs text-purple-300 hover:text-purple-200"
          @click="markAllAsRead"
        >
          {{ $t('common.notification_dropdown.mark_all_read') }}
        </button>
      </div>

      <div class="h-[1px] bg-gradient-to-r from-transparent via-white/25 to-transparent" />

      <div
        v-if="userNotificationsStore.isLoading"
        class="px-4 py-6 text-sm text-gray-300 text-center"
      >
        {{ $t('common.notification_dropdown.loading') }}
      </div>

      <div
        v-else-if="visibleNotifications.length === 0"
        class="px-4 py-6 text-sm text-gray-300 text-center"
      >
        {{ $t('common.notification_dropdown.empty') }}
      </div>

      <div
        v-else
        class="max-h-96 overflow-y-auto"
      >
        <div
          v-for="notification in visibleNotifications"
          :key="notification.id"
          class="px-4 pt-3 hover:bg-purple-500/15 transition-colors cursor-pointer"
          :class="{ 'bg-purple-500/[0.08]': !isReadDisplay(notification) }"
          @click="openNotification(notification)"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold text-white truncate">
                {{ notification.title }}
              </p>
              <p class="text-xs text-gray-300 mt-1 line-clamp-2">
                {{ notification.message }}
              </p>
              <p class="text-[11px] text-gray-400 mt-2">
                {{ notification.createdAt }}
              </p>
            </div>
            <div
              v-if="!isReadDisplay(notification)"
              class="w-2 h-2 rounded-full bg-purple-400 mt-2"
            />
          </div>
          <div class="mt-3 h-[1px] bg-gradient-to-r from-transparent via-white/25 to-transparent" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useUserNotificationsStore } from '@/stores/user-notifications'
import { toast } from '@/utils/toast'

const router = useRouter()
const { t, locale } = useI18n()
const authStore = useAuthStore()
const userNotificationsStore = useUserNotificationsStore()

const rootRef = ref<HTMLElement | null>(null)
const isOpen = ref(false)

const unreadCountDisplay = computed(() => userNotificationsStore.unreadCountDisplay)
const sortedNotifications = computed(() => {
  const toTimestamp = (value: string | Date) => {
    const time = new Date(value).getTime()
    return Number.isNaN(time) ? 0 : time
  }

  return [...userNotificationsStore.items].sort((a, b) => {
    const aRead = isReadDisplay(a)
    const bRead = isReadDisplay(b)

    // Unread notifications first.
    if (aRead !== bRead) {
      return aRead ? 1 : -1
    }

    // Newer notifications first within the same read group.
    return toTimestamp(b.createdAt) - toTimestamp(a.createdAt)
  })
})

const visibleNotifications = computed(() => sortedNotifications.value.slice(0, 8))

const isReadDisplay = (notification: any) => {
  return userNotificationsStore.isNotificationReadDisplay(notification)
}

const toggleOpen = async () => {
  isOpen.value = !isOpen.value
  if (!isOpen.value) return

  await userNotificationsStore.refreshAll({ page: 1, limit: userNotificationsStore.pagination.limit || 20 })
}

const markAllAsRead = async () => {
  try {
    await userNotificationsStore.markAllAsRead()
    toast.success(t('common.notification_dropdown.toast_mark_all_read_success'))
  } catch (error) {
    console.error('Mark all notifications as read failed', error)
    toast.error(t('common.notification_dropdown.toast_mark_all_read_failed'))
  }
}

const openNotification = async (notification: any) => {
  if (!isReadDisplay(notification)) {
    try {
      await userNotificationsStore.markAsRead(notification.id)
    } catch (error) {
      console.error('Mark notification as read failed', error)
    }
  }

  isOpen.value = false

  const actionUrl = notification.actionUrl
  if (!actionUrl) return

  router.push(actionUrl).catch(() => {})
}

const handleClickOutside = (event: Event) => {
  const target = event.target as Node
  if (rootRef.value && !rootRef.value.contains(target)) {
    isOpen.value = false
  }
}

watch(
  () => authStore.user?.id,
  (userId) => {
    if (!userId) {
      isOpen.value = false
      userNotificationsStore.resetState()
      return
    }

    void userNotificationsStore.initializeForCurrentUser()
  },
  { immediate: true },
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
